import type { AskHelixNumericBindingDto } from '../../ask-helix';
import type { RuntimeV3ClaimRevisionRef } from '../../runtime-v3/contracts';
import type { ASK_VIEW_STATUS_LABELS } from './view-payload';

/** Full source identity; array order is irrelevant and pins never grant read authority. */
export type RecoveryCompareSourceExpectation = Readonly<{
  expectedSourceRevisions?: readonly RuntimeV3ClaimRevisionRef[];
}>;
export type RecoverySourceRevisionConflict = Readonly<{
  code: 'source_revision_changed'; message: string;
}>;

/** Engine-authored value and display; the client never derives either. */
export type RecoveryViewFigure = Readonly<{
  rowKey: string; measureId: string; value: number;
  display: string; shortDisplay: string; unit: string;
  numericBinding: AskHelixNumericBindingDto;
}>;
export type RecoveryInputProvenance = Readonly<{ kind: string; ref: string }>;
export type AskViewRecoveryOptionsBlock = Readonly<{
  kind: 'recovery-options';
  /** Exact identity of the displayed baseline, forwarded unchanged when pricing. */
  sourceRevisions: readonly RuntimeV3ClaimRevisionRef[];
  commitment: Readonly<{ id: string; label: string }>;
  baseline: Readonly<{
    figure: RecoveryViewFigure;
    sourcePeriod: string; caption: string; vendors: readonly string[];
    uncoveredComponents: readonly Readonly<{
      label: string; reason: string;
      /** Raw source identity for tracing only; never render it, including in tooltips. */
      ref: string;
    }>[];
  }> | null;
  signals: readonly Readonly<{ id: string; type: string; label: string }>[];
  levers: readonly Readonly<{
    lever: string; label: string; reversible: boolean; reversibilityMeaning: string;
    inputs: readonly Readonly<{
      name: string; description: string; label: string; availability: 'available' | 'missing';
      /** Governed inputs come from the ledger; stated inputs may be supplied in the form. */
      origin: 'governed' | 'stated';
      /** Server guidance for a triplet; null for boolean and ledger inputs. */
      bandHint: string | null;
      /** Retained performance status; never use it to display input availability. */
      status: (typeof ASK_VIEW_STATUS_LABELS)[keyof typeof ASK_VIEW_STATUS_LABELS];
      provenance: RecoveryInputProvenance | null;
    }>[];
  }>[];
  pricing: Readonly<{
    route: 'v1/ontology/decision/compare';
    scope: Readonly<{ dealId: string; commitmentId: string; asOf: string }>;
  }>;
}>;

/** All positions share one server-authored net-recovery axis, including zero. */
export type PricedRecoveryRange = Readonly<{
  label: string; measureLabel: string; pointEstimate: boolean;
  rows: Readonly<{ low: RecoveryViewFigure; base: RecoveryViewFigure; high: RecoveryViewFigure }>;
  geometry: Readonly<{ low: number; base: number; high: number; zero: number; width: number }>;
}>;
export type PricedRecoveryOption = Readonly<{
  lever: string; label: string; reversible: boolean; reversibilityMeaning: string;
  resultClass: 'modeled';
  firstYear: PricedRecoveryRange; restOfYear: PricedRecoveryRange;
}>;
/** Feasible evaluation omitted from the page's top-three priced options. */
export const RECOVERY_OPTION_LIMIT_REASON = 'beyond_option_limit' as const;
export type PricedOptionsView = Readonly<{
  options: readonly PricedRecoveryOption[];
  excluded: readonly Readonly<{ lever: string; label: string; reasons: readonly string[];
    reasonKind?: typeof RECOVERY_OPTION_LIMIT_REASON }>[];
  summary: Readonly<{ verdict: string; why: string; next: string }>;
  formSummary: string;
  /** No range annotation when no options are priced. */
  scaleCaption: string | null; zeroLabel: string | null;
  ordering: string; orderingKey: string; disclosures: readonly string[];
}>;
